import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';

const UsersContext = createContext(null);

export function useManagedUsers() {
  const ctx = useContext(UsersContext);
  if (!ctx) throw new Error('useManagedUsers must be used within UsersProvider');
  return ctx;
}

export function UsersProvider({ children }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const refresh = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await apiJson('/team/users');
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUsers([]);
      setUsersLoading(false);
      return;
    }
    void refresh();
  }, [user, refresh]);

  const addUser = useCallback(
    async (payload) => {
      try {
        await apiJson('/team/users', {
          method: 'POST',
          body: JSON.stringify({
            name: payload.name,
            email: payload.email || undefined,
            phone: payload.phone || undefined,
            role: payload.role,
            password: payload.password,
            branchId: payload.branchId,
          }),
        });
        await refresh();
        return { ok: true };
      } catch (err) {
        const msg = err?.body?.message || err?.message || 'Could not add user.';
        return { ok: false, error: msg };
      }
    },
    [refresh]
  );

  const updateUser = useCallback(
    async (id, payload) => {
      try {
        const body = {
          name: payload.name,
          email: payload.email || undefined,
          phone: payload.phone || undefined,
          role: payload.role,
        };
        if (payload.branchId != null && payload.branchId !== '') {
          body.branchId = Number(payload.branchId);
        }
        if (payload.password && String(payload.password).length >= 8) {
          body.password = payload.password;
        }
        await apiJson(`/team/users/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        await refresh();
        return { ok: true };
      } catch (err) {
        const msg = err?.body?.message || err?.message || 'Could not update user.';
        return { ok: false, error: msg };
      }
    },
    [refresh]
  );

  const removeUser = useCallback(
    async (id) => {
      try {
        await apiJson(`/team/users/${id}`, { method: 'DELETE' });
        await refresh();
      } catch {
        /* ignore */
      }
    },
    [refresh]
  );

  const value = useMemo(
    () => ({
      users,
      usersLoading,
      addUser,
      updateUser,
      removeUser,
      refreshUsers: refresh,
    }),
    [users, usersLoading, addUser, updateUser, removeUser, refresh]
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}
